import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { TokenService } from "./token.service";
import { ErrorHandlerService } from "./error-handler.service";
import { authService } from "./auth.service";
import { getDeviceFingerprint } from "./device-fingerprint.service";
import { User } from "oidc-client-ts";
import { userManager } from "../config/cognito";
import { ApiError } from "../types/errors";

const AUTH_API_BASE_URL: string = import.meta.env.VITE_AUTH_API_URL;
const ADMIN_API_BASE_URL: string = import.meta.env.VITE_ADMIN_API_URL;
const PROJECTS_API_BASE_URL: string = import.meta.env.VITE_PROJECTS_API_URL;
const AUDIT_API_BASE_URL: string = import.meta.env.VITE_AUDIT_API_URL;

/**
 * Create axios instance with base configuration
 */
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: 30000, // 30 seconds
  });

  /**
   * Request Interceptor
   */
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const internalToken: string | null = TokenService.getInternalToken();
      const cognitoUser: User | null = await userManager.getUser();
      const isAuthApi: boolean = baseURL === AUTH_API_BASE_URL;
      const authToken: string | null | undefined = isAuthApi
        ? cognitoUser?.id_token
        : internalToken;

      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      if (isAuthApi) {
        config.headers["X-Device-Fingerprint"] = getDeviceFingerprint();
      }

      return config;
    },
  );

  /**
   * Response Interceptor
   */
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error) => {
      const isOnCallbackPage: boolean =
        window.location.pathname === "/auth/callback" ||
        window.location.pathname.includes("/auth/callback");
      const apiError: ApiError = ErrorHandlerService.handleError(error);

      const responseCode = error.response?.data?.code;

      // Handle 401 Unauthorized — usually invalid/expired session.
      // Only logout on session/authentication failures. Some endpoints incorrectly return 401
      // for per-resource denial (e.g. accessing a disabled/pending user's related resources),
      // which should not terminate the viewer's session.
      if (error.response?.status === 401) {
        const shouldLogout =
          responseCode === "NOT_AUTHENTICATED" ||
          responseCode === "TOKEN_REVOKED" ||
          responseCode === "USER_DEACTIVATED" ||
          responseCode === "ORG_ACCESS_REVOKED" ||
          responseCode === "INVALID_USER_CONTEXT";
        if (shouldLogout) {
          const logoutReason =
            responseCode === "ORG_ACCESS_REVOKED"
              ? "org_access_changed"
              : "absolute";
          void authService.logout(logoutReason);
        }
        return Promise.reject(apiError);
      }

      if (error.response?.status === 403) {
        // IMPORTANT:
        // Many endpoints return 403 for business-rule/authorization errors on a specific resource
        // (e.g. viewing statistics for users without project activity). Those must NOT terminate
        // the current session. Only logout when the backend explicitly tells us the session/context
        // is invalid via a known error code.
        const shouldLogout =
          responseCode === "NOT_AUTHENTICATED" ||
          responseCode === "TOKEN_REVOKED" ||
          responseCode === "USER_DEACTIVATED" ||
          responseCode === "ORG_ACCESS_REVOKED" ||
          responseCode === "INVALID_USER_CONTEXT";
        if (shouldLogout) {
          const logoutReason =
            responseCode === "ORG_ACCESS_REVOKED"
              ? "org_access_changed"
              : "absolute";
          void authService.logout(logoutReason);
        }
        return Promise.reject(apiError);
      }

      // Only show global toast for server errors (>=500) when not explicitly skipped
      const status = error.response?.status;
      const skipGlobalToast = (error.config as any).__skipGlobalErrorToast;
      const isServerError = typeof status === "number" && status >= 500;
      const isAuthError = status === 401;

      if (
        isServerError &&
        !skipGlobalToast &&
        !(isAuthError && isOnCallbackPage)
      ) {
        // Import dynamically to avoid circular dependency
        import("./global-error-toast.service")
          .then(({ globalErrorToastService }) => {
            if (globalErrorToastService.isInitialized()) {
              globalErrorToastService.showError(apiError);
            }
          })
          .catch((): void => {});
      }

      return Promise.reject(apiError);
    },
  );

  return instance;
};

/**
 * API Client Instances for each backend service
 * Auth API uses withCredentials for trusted device cookie (evoly_device_fp)
 */
export const authApiClient: AxiosInstance =
  createAxiosInstance(AUTH_API_BASE_URL);
export const adminApiClient: AxiosInstance =
  createAxiosInstance(ADMIN_API_BASE_URL);
export const projectsApiClient: AxiosInstance = createAxiosInstance(
  PROJECTS_API_BASE_URL,
);
export const auditApiClient: AxiosInstance =
  createAxiosInstance(AUDIT_API_BASE_URL);
